import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FieldError } from 'react-hook-form';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { CircularProgress } from '@mui/material';
import { useRetrieveListByGet } from '../../hooks/reactQuery/useRetrieveListByGet';

interface Option {
  id: string;
  name: string;
}

export interface ICasAutoCompleteAsyncProps<T = Option>
  extends Omit<
    AutocompleteProps<T, false, false, false>,
    'renderInput' | 'options' | 'value' | 'onInputChange'
  > {
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  debounceTime?: number;
  onValueChange: (event: React.SyntheticEvent, value: T | null) => void;
  defaultValue?: T | null;
  url: string;
  searchKeyword?: string;
  width?: number | string;
  error?: FieldError;
}

export default function CasAutoCompleteAsync<T>({
  id,
  label,
  placeholder,
  helperText,
  debounceTime = 300,
  onValueChange,
  defaultValue,
  url,
  searchKeyword = 'search',
  width = 250,
  error,
  autoFocus,
  ...other
}: ICasAutoCompleteAsyncProps<T>) {
  const [options, setOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState<T | null>(defaultValue || null);
  const [debouncedValue, setDebouncedValue] = useState('');

  // Add useEffect to handle defaultValue properly
  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      // Set input value to match the default value's display text
      setInputValue(typeof defaultValue === 'string' 
        ? defaultValue 
        : (defaultValue as any)?.name || '');
    }
  }, [defaultValue]);

  // Debounced input value change
  const debouncedInputChange = useDebouncedCallback((newValue: string) => {
    setDebouncedValue(newValue);
  }, debounceTime);

  // Fetch data using the debounced input value
  const { data, isLoading } = useRetrieveListByGet({
    url,
    params: { 
      id,
      [searchKeyword]: debouncedValue,
      pageSize: 50,
      pageNumber:1
    },
    staleTime: 0,
    enabled: debouncedValue !== '',
  })

  // Update options when data changes
  useEffect(() => {
    if (data && debouncedValue !== '') {
      setOptions(data || []);
    }
  }, [data, debouncedValue]);

  // Clear options if debouncedValue is empty
  useEffect(() => {
    if (!debouncedValue) {
      setOptions([]);
    }
  }, [debouncedValue]);

  return (
    <Autocomplete
      {...other}
      sx={{ width }}
      options={options}
      loading={isLoading}
      value={value}
      getOptionLabel={(option: any) => {
        if (option?.name) return option.name;
        return option ?? '';
      }}
      isOptionEqualToValue={(option: any, selectedValue: any) =>
        option?.id === selectedValue?.id
      }
      onChange={(event: React.SyntheticEvent, newValue: any) => {
        setValue(newValue);
        onValueChange(event, newValue || null);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        debouncedInputChange(newInputValue);
      }}
      noOptionsText={
        debouncedValue === ''
          ? 'Enter a search string'
          : 'No matches'
      }
      loadingText="Loading..."
      renderInput={(params) => (
        <TextField
          {...(params as TextFieldProps)}
          error={!!error}
          autoFocus={autoFocus}
          helperText={error ? error?.message : helperText}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
